import { call, put, takeLatest } from 'redux-saga/effects';
import { ANCILLARY_FILTER_DATA_REQUEST, ANCILLARY_CAT_DETAILS_DATA_REQUEST, ANCILLARY_NET_CAT_DETAILS_DATA_REQUEST, ANCILLARY_EXPORT_LINK_REQUEST, ANCILLARY_CATEGORY_VIEW_DATA_REQUEST } from '../../actions/actionTypes';
import { labsFilterDataSuccess, labsFilterDataError, labsFilterDataLoading, labsDetailsDataSuccess, labsDetailsDataError, labsDetailsDataLoading, labsNetDetailsDataSuccess, labsNetDetailsDataError, labsNetDetailsDataLoading, labsDetailsExportLoading, labsDetailsExportSuccess, labsDetailsExportError, labsCategoryViewLoading, labsCategoryViewError, labsCategoryViewSuccess } from '../../actions/Ancillary/Labs/LabsAction';
import { getTopFilterData, getDateFilterData, getLabcategoryData, getNetLabcategoryData, getExporDetails, getExporDetailsStatus, getExporDetailsLinks, getLabCatViewData } from '../../services/Ancillary/Labs/labs.service';

export function* fetchTopFilterDetails(action: any) {
  try {    
    yield put(labsFilterDataLoading(true));    

    // Call the API services
    const response = yield call(getDateFilterData);
    let topFilterPayload = action?.payload;
    if (!topFilterPayload?.NetworkLeverTopFilter?.year && response?.Data) {
      const defaultPeriod = response.Data.find((item: any) => item.Default === true);
      if (defaultPeriod) {
        topFilterPayload = {
          ...topFilterPayload,
          NetworkLeverTopFilter: {
            ...topFilterPayload?.NetworkLeverTopFilter,
            year: new Date(defaultPeriod.StartDate).getFullYear()
          }
        };
      }
    }

    const topFilter = yield call(getTopFilterData, topFilterPayload);

    // Reset loading state
    yield put(labsFilterDataLoading(false));

    if ((!response || response?.Status !== "OK") || (!topFilter || topFilter?.Status !== "OK")) {
      // Handle error
      yield put(labsFilterDataError("Failed to fetch data"));
    } else {
      // Handle success - combine both responses
      const filterData = {
        dateFilter: response?.Data,
        topFilter: topFilter?.Data
      };
      yield put(labsFilterDataSuccess(filterData));
    }
  } catch (error) {
    yield put(labsFilterDataLoading(false));
    yield put(labsFilterDataError("An error occurred"));
  }
}

export function* fetchLabCategoryDetails(action: any) {
  try {
    yield put(labsDetailsDataLoading(true));    

    // Call the API services
    const preferredResponse = yield call(getLabcategoryData, { ...action?.payload, TypeOfService: "Preferred" });
    const nonPreferredResponse = yield call(getLabcategoryData, { ...action?.payload, TypeOfService: "Non-Preferred" });

    // Reset loading state
    yield put(labsDetailsDataLoading(false));

    if ((!preferredResponse || preferredResponse?.Status !== "OK") || (!nonPreferredResponse || nonPreferredResponse?.Status !== "OK")) {
      // Handle error
      yield put(labsDetailsDataError("Failed to fetch data"));
    } else {
      // Handle success - combine both responses
      const Data = {
        preferred: preferredResponse?.Data,
        nonPreferred: nonPreferredResponse?.Data
      };
   
      yield put(labsDetailsDataSuccess(Data));
    }
  } catch (error) {
    yield put(labsDetailsDataLoading(false));
    yield put(labsDetailsDataError("An error occurred"));
  }
}

export function* fetchLabNetCategoryDetails(action: any) {
  try {
    yield put(labsNetDetailsDataLoading(true));    

    // Call the API services
    const preferredResponse = yield call(getNetLabcategoryData, { ...action?.payload, TypeOfService: "Preferred" });
    const nonPreferredResponse = yield call(getNetLabcategoryData, { ...action?.payload, TypeOfService: "Non-Preferred" });

    // Reset loading state
    yield put(labsNetDetailsDataLoading(false));

    if ((!preferredResponse || preferredResponse?.Status !== "OK") || (!nonPreferredResponse || nonPreferredResponse?.Status !== "OK")) {
      // Handle error
      yield put(labsNetDetailsDataError("Failed to fetch data"));
    } else {
      // Handle success - combine both responses
      const Data = {
        preferred: preferredResponse?.Data,
        nonPreferred: nonPreferredResponse?.Data
      };
   
      yield put(labsNetDetailsDataSuccess(Data));
    }
  } catch (error) {
    yield put(labsNetDetailsDataLoading(false));
    yield put(labsNetDetailsDataError("An error occurred"));
  }
}

export function* fetchLabDetailsExportLinks(action: any) {
  try {
    yield put(labsDetailsExportLoading(true));    

    // Step 1: Call getExporDetails API
    // Filter out empty values and add required fields
    const filteredPayload = Object.entries({ ...action?.payload }).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    
    const exportPayload = { ...filteredPayload, OutputFormat: "ND_JSON", ViewType: "TAXID" };
    const exportDetailsResponse = yield call(getExporDetails, exportPayload);

    if (!exportDetailsResponse || exportDetailsResponse?.Status !== "OK") {
      yield put(labsDetailsExportLoading(false));
      yield put(labsDetailsExportError("Failed to initiate export"));
      return;
    }

    const exportId = exportDetailsResponse?.Data?.Id;
    
    if (!exportId) {
      yield put(labsDetailsExportLoading(false));
      yield put(labsDetailsExportError("Export ID not received"));
      return;
    }

    // Step 2: Poll getExporDetailsStatus API with the received ID
    let maxRetries = 100;
    let currentRetry = 0;
    let isCompleted = false;
    let statusResponse;

    while (currentRetry < maxRetries && !isCompleted) {
      const statusPayload = { Id: exportId };
      
      statusResponse = yield call(getExporDetailsStatus, statusPayload);

      if (!statusResponse || statusResponse?.Status !== "OK") {
        yield put(labsDetailsExportLoading(false));
        yield put(labsDetailsExportError("Failed to fetch export status"));
        return;
      }

      // Check if Data is null - stop polling
      if (statusResponse?.Data === null || statusResponse?.Data === undefined) {
        yield put(labsDetailsExportLoading(false));
        yield put(labsDetailsExportError("Export data not available"));
        return;
      }

      // Check if status is completed
      const exportStatus = statusResponse?.Data?.Status;
      
      if (exportStatus === "Completed") {
        isCompleted = true;
      } else {
        currentRetry++;
        if (currentRetry < maxRetries) {
          // Wait for 3 seconds before next retry
          yield new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
    }

    // Check if export completed or max retries reached
    if (!isCompleted) {
      yield put(labsDetailsExportLoading(false));
      yield put(labsDetailsExportError("Export process timed out. Please try again later."));
      return;
    }

    // Step 3: Call getExporDetailsLinks API
    const linksPayload = { Id: exportId };
    
    const linksResponse = yield call(getExporDetailsLinks, linksPayload);

    // Reset loading state
    yield put(labsDetailsExportLoading(false));

    if (!linksResponse || linksResponse?.Status !== "OK") {
      yield put(labsDetailsExportError("Failed to fetch export links"));
    } else {
      yield put(labsDetailsExportSuccess(linksResponse?.Data));
    }
    
  } catch (error) {
    yield put(labsDetailsExportLoading(false));
    yield put(labsDetailsExportError("An error occurred during export"));
  }
}

export function* fetchLabCategoryViewData(action: any) {
  try {
    yield put(labsCategoryViewLoading(true));    

    // Call the API services
    const preferredResponse = yield call(getLabCatViewData, { ...action?.payload, TypeOfService: "Preferred" });
    const nonPreferredResponse = yield call(getLabCatViewData, { ...action?.payload, TypeOfService: "Non-Preferred" });

    // Reset loading state
    yield put(labsCategoryViewLoading(false));

    if ((!preferredResponse || preferredResponse?.Status !== "OK") || (!nonPreferredResponse || nonPreferredResponse?.Status !== "OK")) {
      // Handle error
      yield put(labsCategoryViewError("Failed to fetch data"));
    } else {
      // Handle success - combine both responses
      const Data = {
        preferred: preferredResponse?.Data,
        nonPreferred: nonPreferredResponse?.Data
      };
   
      yield put(labsCategoryViewSuccess(Data));
    }
  } catch (error) {
    yield put(labsCategoryViewLoading(false));
    yield put(labsCategoryViewError("An error occurred"));
  }
}


export function* LabsSaga() {
  yield takeLatest(ANCILLARY_FILTER_DATA_REQUEST, fetchTopFilterDetails);
  yield takeLatest(ANCILLARY_CAT_DETAILS_DATA_REQUEST, fetchLabCategoryDetails);
  yield takeLatest(ANCILLARY_NET_CAT_DETAILS_DATA_REQUEST, fetchLabNetCategoryDetails);
  yield takeLatest(ANCILLARY_EXPORT_LINK_REQUEST, fetchLabDetailsExportLinks);
  yield takeLatest(ANCILLARY_CATEGORY_VIEW_DATA_REQUEST, fetchLabCategoryViewData);


}

export default [LabsSaga];
